import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Chip, Modal, ModalBody, ModalContent, Spinner } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { type FC, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import toast from '@/app/toast.ts';
import type {
  OrganizationAccountReportingModel,
  OrganizationReportingModel,
} from '@/app/types/organization.type.ts';
import { Field, Fieldset, Label } from '@/primitives/fieldset.tsx';
import { Input } from '@/primitives/input.tsx';
import { Text } from '@/primitives/text.tsx';
import { useFetchOrganizationReportingQuery } from '@/slices/organization-api-slice.ts';

interface OrganizationReportingDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  organizationKey?: string;
  organizationName: string;
}

const copyTextToClipboard = async (value: string, successMessage: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(successMessage);
  } catch {
    toast.error('Failed to copy to clipboard');
  }
};

interface CopyableKeyRowProps {
  label: string;
  keyValue: string;
}

const CopyableKeyRow: FC<CopyableKeyRowProps> = ({ label, keyValue }) => (
  <div className='flex min-w-0 flex-1 items-center gap-2'>
    <Text className='break-all font-mono text-sm italic text-zinc-500 dark:text-zinc-400'>
      {keyValue}
    </Text>
    <Button
      isIconOnly
      size='sm'
      color='primary'
      variant='light'
      aria-label={`Copy ${label}`}
      onPress={() => copyTextToClipboard(keyValue, `${label} copied`)}
    >
      <ClipboardDocumentIcon className='size-5' />
    </Button>
  </div>
);

interface SectionHeaderProps {
  index: string;
  title: string;
}

const SectionHeader: FC<SectionHeaderProps> = ({ index, title }) => (
  <p className='text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-secondary-600 dark:text-secondary-400'>
    {index} / {title}
  </p>
);

interface StatCellProps {
  label: string;
  value: string;
}

const StatCell: FC<StatCellProps> = ({ label, value }) => (
  <div className='bg-white px-4 py-3 dark:bg-zinc-900'>
    <dt className='text-xs font-medium uppercase leading-tight tracking-wide text-gray-500 dark:text-zinc-400'>
      {label}
    </dt>
    <dd className='mt-1 text-xl font-semibold tabular-nums text-zinc-900 dark:text-white'>
      {value}
    </dd>
  </div>
);

const formatCount = (value?: number | null) =>
  value === undefined || value === null ? '—' : String(value);

const formatRate = (numerator: number, denominator: number) =>
  denominator > 0 ? `${((numerator / denominator) * 100).toFixed(1)}%` : '—';

type DateRangePreset = 'currentMonth' | 'lastMonth' | 'last3Months' | 'last6Months' | 'lastYear';

interface DateRange {
  start: string;
  end: string;
}

const toIsoDate = (date: DateTime) => date.toISODate() ?? '';

const getDateRangeForPreset = (preset: DateRangePreset): DateRange => {
  const now = DateTime.now();

  switch (preset) {
    case 'currentMonth':
      return {
        start: toIsoDate(now.startOf('month')),
        end: toIsoDate(now),
      };
    case 'lastMonth': {
      const lastMonth = now.minus({ months: 1 });
      return {
        start: toIsoDate(lastMonth.startOf('month')),
        end: toIsoDate(lastMonth.endOf('month')),
      };
    }
    case 'last3Months':
      return {
        start: toIsoDate(now.minus({ months: 2 }).startOf('month')),
        end: toIsoDate(now.endOf('month')),
      };
    case 'last6Months':
      return {
        start: toIsoDate(now.minus({ months: 5 }).startOf('month')),
        end: toIsoDate(now.endOf('month')),
      };
    case 'lastYear':
      return {
        start: toIsoDate(now.minus({ months: 12 })),
        end: toIsoDate(now),
      };
  }
};

const dateRangePresets: { id: DateRangePreset; label: string }[] = [
  { id: 'currentMonth', label: 'Current Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'last3Months', label: 'Last 3 Months' },
  { id: 'last6Months', label: 'Last 6 Months' },
  { id: 'lastYear', label: 'Last Year' },
];

const defaultDateRange = (): DateRange => getDateRangeForPreset('lastYear');

const OrganizationReportingDialog: FC<OrganizationReportingDialogProps> = ({
  isOpen,
  setIsOpen,
  organizationKey,
  organizationName,
}) => {
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset | null>('lastYear');

  useEffect(() => {
    if (isOpen) {
      setDateRange(defaultDateRange());
      setSelectedPreset('lastYear');
    }
  }, [isOpen]);

  const queryArgs =
    isOpen && organizationKey && dateRange.start && dateRange.end
      ? { key: organizationKey, start: dateRange.start, end: dateRange.end }
      : skipToken;

  const { data, isLoading, isFetching, isError } = useFetchOrganizationReportingQuery(queryArgs);

  useEffect(() => {
    if (isError) {
      toast.error('There was an error loading organization reporting data.');
    }
  }, [isError]);

  const isInitialLoad = isLoading && !data;
  const isRefreshing = isFetching && !!data;
  const orgKeyForCopy = data?.key ?? organizationKey;

  const applyPreset = (preset: DateRangePreset) => {
    setSelectedPreset(preset);
    setDateRange(getDateRangeForPreset(preset));
  };

  const updateDateRangeField = (field: keyof DateRange, value: string) => {
    setSelectedPreset(null);
    setDateRange(current => ({ ...current, [field]: value }));
  };

  return (
    <Modal
      classNames={{
        base: 'h-[90vh] max-h-[90vh] overflow-hidden',
        body: 'min-h-0 flex-1 overflow-y-auto',
      }}
      hideCloseButton
      isOpen={isOpen}
      scrollBehavior='inside'
      size='5xl'
      onClose={() => setIsOpen(false)}
    >
      <ModalContent className='flex h-[90vh] max-h-[90vh] flex-col overflow-hidden p-0'>
        <div className='relative flex shrink-0 items-center justify-center rounded-t-large border-b border-gray-200 bg-gray-100 px-6 py-2.5 dark:border-zinc-700 dark:bg-zinc-800'>
          <h2 className='text-base font-semibold text-zinc-950 dark:text-white'>Reporting Data</h2>
          <Button
            isIconOnly
            size='sm'
            variant='light'
            className='absolute right-3 top-1/2 -translate-y-1/2'
            onPress={() => setIsOpen(false)}
          >
            <XMarkIcon className='size-5' />
          </Button>
        </div>

        <ModalBody className='flex min-h-0 flex-1 flex-col px-6 pb-6 pt-4'>
          <header className='mb-4 shrink-0 border-b border-gray-100 pb-3 dark:border-zinc-800'>
            <h3 className='text-xl font-bold text-zinc-950 dark:text-white'>{organizationName}</h3>
            {orgKeyForCopy && <CopyableKeyRow label='Org key' keyValue={orgKeyForCopy} />}
          </header>

          <Fieldset className='mb-6 shrink-0 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between'>
            <div className='flex flex-col gap-4 sm:flex-row sm:items-end'>
              <Field>
                <Label>Start</Label>
                <Input
                  type='date'
                  value={dateRange.start}
                  onChange={event => updateDateRangeField('start', event.target.value)}
                />
              </Field>
              <Field>
                <Label>End</Label>
                <Input
                  type='date'
                  value={dateRange.end}
                  onChange={event => updateDateRangeField('end', event.target.value)}
                />
              </Field>
            </div>
            <div className='flex flex-wrap gap-2'>
              {dateRangePresets.map(preset => {
                const isSelected = selectedPreset === preset.id;

                return (
                  <Button
                    key={preset.id}
                    size='sm'
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'solid' : 'flat'}
                    onPress={() => applyPreset(preset.id)}
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </div>
          </Fieldset>

          <div className='relative min-h-0 flex-1'>
            {isInitialLoad && (
              <div className='flex h-full min-h-[50vh] items-center justify-center'>
                <Spinner size='lg' label='Loading reporting data...' />
              </div>
            )}
            {data && (
              <div className={clsx('relative', isRefreshing && 'pointer-events-none opacity-60')}>
                {isRefreshing && (
                  <div className='absolute inset-x-0 top-0 z-10 flex justify-center pt-8'>
                    <Spinner size='md' />
                  </div>
                )}
                <ReportingContent data={data} />
              </div>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

interface ReportingContentProps {
  data: OrganizationReportingModel;
}

const ReportingContent: FC<ReportingContentProps> = ({ data }) => {
  const summary = data.summary;
  const confirmationDenominator = summary.meetingsConfirmed + summary.meetingsCanceled;
  const confirmationRate = formatRate(summary.meetingsConfirmed, confirmationDenominator);
  const slotConversionRate = formatRate(summary.meetingsFromSlots, summary.slotsCreated);
  const hasMonthlyTrends = data.monthlyTrends.length > 0;
  const hasAccountBreakdown = data.accountBreakdown.length > 0;

  const chartData = useMemo(
    () =>
      data.monthlyTrends.map(month => ({
        label: month.label,
        slotsCreated: month.slotsCreated,
        meetingsConfirmed: month.meetingsConfirmed,
        meetingsCompleted: month.meetingsCompleted,
        meetingsCanceled: month.meetingsCanceled,
      })),
    [data.monthlyTrends],
  );

  return (
    <div className='flex flex-col pb-6'>
      <section className='flex flex-col gap-3 pb-8'>
        <SectionHeader index='01' title='Organization Overview' />
        <dl className='grid gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 sm:grid-cols-2 lg:grid-cols-3 dark:border-zinc-700 dark:bg-zinc-700'>
          <StatCell label='Total accounts' value={formatCount(summary.totalAccounts)} />
          <StatCell label='Active accounts' value={formatCount(summary.activeAccounts)} />
          <StatCell
            label='Approved rep connections'
            value={formatCount(summary.approvedRepConnections)}
          />
          <StatCell label='Pending rep requests' value={formatCount(summary.pendingRepRequests)} />
          <StatCell label='Slots created' value={formatCount(summary.slotsCreated)} />
          <StatCell label='Meetings requested' value={formatCount(summary.meetingsRequested)} />
          <StatCell label='Meetings confirmed' value={formatCount(summary.meetingsConfirmed)} />
          <StatCell label='Meetings completed' value={formatCount(summary.meetingsCompleted)} />
          <StatCell label='Meetings canceled' value={formatCount(summary.meetingsCanceled)} />
          <StatCell label='Meetings from slots' value={formatCount(summary.meetingsFromSlots)} />
          <StatCell label='Confirmation rate' value={confirmationRate} />
          <StatCell label='Slot conversion rate' value={slotConversionRate} />
        </dl>
      </section>

      <section className='flex flex-col gap-4 border-t border-gray-300 py-8 dark:border-zinc-600'>
        <SectionHeader index='02' title='Monthly Activity' />
        {hasMonthlyTrends ? (
          <>
            <div className='h-72 w-full'>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='label' tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey='slotsCreated' name='Slots' fill='#4b5563' radius={[4, 4, 0, 0]} />
                  <Bar
                    dataKey='meetingsConfirmed'
                    name='Confirmed'
                    fill='#65a30d'
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey='meetingsCompleted'
                    name='Completed'
                    fill='#2563eb'
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey='meetingsCanceled'
                    name='Canceled'
                    fill='#dc2626'
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <MonthlyTrendsTable monthlyTrends={data.monthlyTrends} />
          </>
        ) : (
          <Text className='text-sm italic text-gray-500 dark:text-zinc-400'>
            No monthly activity in this date range
          </Text>
        )}
      </section>

      <section className='flex flex-col gap-3 border-t border-gray-300 pt-8 dark:border-zinc-600'>
        <SectionHeader index='03' title='By Account' />
        {hasAccountBreakdown ? (
          <ul className='flex flex-col gap-5'>
            {data.accountBreakdown.map(account => (
              <AccountReportingItem key={account.accountKey} account={account} />
            ))}
          </ul>
        ) : (
          <Text className='text-sm italic text-gray-500 dark:text-zinc-400'>
            No accounts in this organization
          </Text>
        )}
      </section>
    </div>
  );
};

interface MonthlyTrendsTableProps {
  monthlyTrends: OrganizationReportingModel['monthlyTrends'];
}

const MonthlyTrendsTable: FC<MonthlyTrendsTableProps> = ({ monthlyTrends }) => (
  <div className='overflow-x-auto rounded-lg border border-gray-200 dark:border-zinc-700'>
    <table className='min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700'>
      <thead className='bg-gray-50 dark:bg-zinc-800/50'>
        <tr>
          <th className='px-3 py-2 text-left font-medium text-gray-600 dark:text-zinc-300'>
            Month
          </th>
          <th className='px-3 py-2 text-right font-medium text-gray-600 dark:text-zinc-300'>
            Slots
          </th>
          <th className='px-3 py-2 text-right font-medium text-gray-600 dark:text-zinc-300'>
            Requested
          </th>
          <th className='px-3 py-2 text-right font-medium text-gray-600 dark:text-zinc-300'>
            Confirmed
          </th>
          <th className='px-3 py-2 text-right font-medium text-gray-600 dark:text-zinc-300'>
            Completed
          </th>
          <th className='px-3 py-2 text-right font-medium text-gray-600 dark:text-zinc-300'>
            Canceled
          </th>
          <th className='px-3 py-2 text-right font-medium text-gray-600 dark:text-zinc-300'>
            From Slots
          </th>
        </tr>
      </thead>
      <tbody className='divide-y divide-gray-100 dark:divide-zinc-800'>
        {monthlyTrends.map(month => (
          <tr key={`${month.year}-${month.month}`}>
            <td className='px-3 py-2 font-medium text-zinc-900 dark:text-white'>{month.label}</td>
            <td className='px-3 py-2 text-right text-zinc-700 dark:text-zinc-300'>
              {month.slotsCreated}
            </td>
            <td className='px-3 py-2 text-right text-zinc-700 dark:text-zinc-300'>
              {month.meetingsRequested}
            </td>
            <td className='px-3 py-2 text-right text-zinc-700 dark:text-zinc-300'>
              {month.meetingsConfirmed}
            </td>
            <td className='px-3 py-2 text-right text-zinc-700 dark:text-zinc-300'>
              {month.meetingsCompleted}
            </td>
            <td className='px-3 py-2 text-right text-zinc-700 dark:text-zinc-300'>
              {month.meetingsCanceled}
            </td>
            <td className='px-3 py-2 text-right text-zinc-700 dark:text-zinc-300'>
              {month.meetingsFromSlots}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

interface AccountReportingItemProps {
  account: OrganizationAccountReportingModel;
}

const AccountReportingItem: FC<AccountReportingItemProps> = ({ account }) => {
  const statusColor = account.isActive ? 'success' : 'default';
  const statusLabel = account.isActive ? 'Active' : 'Inactive';

  return (
    <li className='rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 dark:border-zinc-700 dark:bg-zinc-800/60'>
      <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
        <span className='text-sm font-semibold text-zinc-950 dark:text-white'>
          {account.accountName}
        </span>
        <Chip size='sm' color={statusColor} variant='flat'>
          {statusLabel}
        </Chip>
      </div>
      <div className='mt-1.5 flex min-w-0 items-center gap-1'>
        <span className='truncate font-mono text-xs text-gray-500 dark:text-zinc-400'>
          {account.accountKey}
        </span>
        <Button
          isIconOnly
          size='sm'
          variant='light'
          className='h-6 w-6 min-w-6 shrink-0 text-gray-500 dark:text-zinc-400'
          aria-label='Copy account key'
          onPress={() => copyTextToClipboard(account.accountKey, 'Account key copied')}
        >
          <ClipboardDocumentIcon className='size-3.5' />
        </Button>
      </div>
      <AccountMetricsGrid account={account} />
    </li>
  );
};

interface AccountMetricsGridProps {
  account: OrganizationAccountReportingModel;
}

const AccountMetricsGrid: FC<AccountMetricsGridProps> = ({ account }) => (
  <dl className='mt-3 grid gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3'>
    <MetricCell label='Slots created' value={account.slotsCreated} />
    <MetricCell label='Requested' value={account.meetingsRequested} />
    <MetricCell label='Confirmed' value={account.meetingsConfirmed} />
    <MetricCell label='Completed' value={account.meetingsCompleted} />
    <MetricCell label='Canceled' value={account.meetingsCanceled} />
    <MetricCell label='From slots' value={account.meetingsFromSlots} />
  </dl>
);

interface MetricCellProps {
  label: string;
  value: number;
}

const MetricCell: FC<MetricCellProps> = ({ label, value }) => (
  <div>
    <dt className='text-[0.65rem] font-medium uppercase leading-tight tracking-wide text-gray-500 dark:text-zinc-400'>
      {label}
    </dt>
    <dd className='mt-0.5 text-sm font-semibold tabular-nums text-zinc-900 dark:text-white'>
      {value}
    </dd>
  </div>
);

export default OrganizationReportingDialog;
